import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useApi, errMsg } from "@/lib/api";
import { fmtDT, inr, statusLabel } from "@/lib/brand";
import { PriceRows, StatusBadge } from "@/components/site/Shared";
import { GoogleTripMap, MAPS_KEY } from "@/components/site/GoogleMaps";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KycBadge } from "@/pages/admin/AdminKyc";
import { Link } from "react-router-dom";
import { InspectionEditor } from "@/components/admin/InspectionEditor";
import { MapPin, Navigation } from "lucide-react";

const NEXT = { PENDING_PAYMENT: ["CANCELLED"], CONFIRMED: ["CANCELLED"], ACTIVE: [] };
const ACTION_LABEL = { ACTIVE: "Start trip", COMPLETED: "Complete trip", CANCELLED: "Cancel" };
const FILTERS = ["", "PENDING_PAYMENT", "CONFIRMED", "ACTIVE", "COMPLETED", "CANCELLED"];

function DriverAssign({ b, onChanged }) {
  const api = useApi();
  const [d, setD] = useState({ name: b.driver?.name || "", phone: b.driver?.phone || "" });
  const save = async () => {
    try { await api.patch(`/admin/bookings/${b.id}/driver`, d); toast.success("Driver assigned"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const cls = "rounded-md border border-white/10 bg-[#0A0A0A] px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#FF5A00]";
  return (
    <div className="space-y-2 rounded-md border border-[#FF5A00]/40 bg-[#FF5A00]/5 p-4" data-testid="admin-driver-assign">
      <p className="text-sm font-semibold">With Driver booking {b.driver ? `· assigned to ${b.driver.name}` : "· driver not assigned yet"}</p>
      <div className="flex flex-wrap gap-2">
        <input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="Driver name" data-testid="admin-driver-name" className={`${cls} flex-1`} />
        <input value={d.phone} onChange={(e) => setD({ ...d, phone: e.target.value })} placeholder="Driver mobile" data-testid="admin-driver-phone" className={`${cls} w-40`} />
        <button onClick={save} data-testid="admin-driver-save" className="rounded-md bg-[#FF5A00] px-4 py-2 text-sm font-semibold text-white">{b.driver ? "Update" : "Assign"}</button>
      </div>
    </div>
  );
}

function TripOtp({ b }) {
  const api = useApi();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const kind = b.status === "CONFIRMED" ? "checkin" : "checkout";
  if (!(["CONFIRMED", "ACTIVE"].includes(b.status))) return null;
  const gen = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/bookings/${b.id}/${kind}-otp`);
      setCode(r.data.otp);
      toast.success(`${kind === "checkin" ? "Check-in" : "Check-out"} code generated`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-3 rounded-md border border-[#D4AF67]/50 bg-[#D4AF67]/10 p-4" data-testid="admin-trip-otp">
      <p className="text-sm font-semibold">{kind === "checkin" ? "Start of trip" : "End of trip"} verification</p>
      <p className="text-xs text-neutral-400">Generate a one-time code and read it out to the customer. They enter it in the app to {kind === "checkin" ? "begin the trip and turn on tracking" : "end the trip"}.</p>
      <button onClick={gen} disabled={busy} data-testid={`admin-gen-${kind}-otp`} className="w-full rounded-md bg-[#0F5132] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60 sm:w-auto">{busy ? "Generating…" : code ? "Regenerate code" : `Generate ${kind === "checkin" ? "check-in" : "check-out"} code`}</button>
      {code && (
        <div className="rounded-lg border border-[#0F5132]/30 bg-white p-4 text-center" data-testid="admin-trip-otp-box">
          <p className="text-xs uppercase tracking-[0.15em] text-neutral-500">{kind === "checkin" ? "Check-in code" : "Check-out code"} — read this to the customer</p>
          <p data-testid="admin-trip-otp-code" className="mt-2 font-mono text-4xl font-bold tracking-[0.35em] text-[#0F5132]">{code}</p>
          <button onClick={() => { navigator.clipboard?.writeText(code); toast.success("Code copied"); }} className="mt-2 text-xs font-semibold text-[#0F5132] underline">Copy code</button>
          <p className="mt-1 text-[11px] text-neutral-400">Valid for 60 minutes</p>
        </div>
      )}
    </div>
  );
}

function LiveLocation({ bookingId }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/admin/bookings/${bookingId}/location`).then((r) => setData(r.data)).catch(() => {}), [api, bookingId]);
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);
  const loc = data?.location;
  const hasPos = loc && loc.lat != null && loc.lng != null;
  const mapsUrl = hasPos ? `https://www.google.com/maps?q=${loc.lat},${loc.lng}` : null;
  return (
    <div className="space-y-2 rounded-md border border-sky-500/40 bg-sky-500/5 p-4" data-testid="admin-booking-live-location">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 text-sm font-semibold"><Navigation className="h-4 w-4 text-sky-400" />Live vehicle location</p>
        {loc && <span className={`text-xs font-semibold ${data.live ? "text-emerald-400" : "text-amber-400"}`}>{data.live ? "● Live now" : "Signal lost"}</span>}
      </div>
      {hasPos ? (
        <>
          {MAPS_KEY && <div className="overflow-hidden rounded-lg"><GoogleTripMap lat={loc.lat} lng={loc.lng} label={data.car_name} /></div>}
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-300">
            <span className="flex items-center gap-1" data-testid="admin-booking-coords"><MapPin className="h-3.5 w-3.5 text-sky-400" />{loc.lat.toFixed(6)}, {loc.lng.toFixed(6)}</span>
            {loc.updated_at && <span className="text-neutral-500">Updated {fmtDT(loc.updated_at)}</span>}
          </div>
          <a href={mapsUrl} target="_blank" rel="noreferrer" data-testid="admin-booking-maps-link" className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 px-3 py-2 text-xs font-semibold text-white">Open exact location in Google Maps</a>
        </>
      ) : (
        <p className="text-xs text-neutral-400" data-testid="admin-booking-location-waiting">Waiting for the first GPS signal from the customer's app. It appears within a minute once the trip is started and location is allowed on their phone.</p>
      )}
    </div>
  );
}

function BookingDialog({ b, onClose, onChanged }) {
  const api = useApi();
  if (!b) return null;
  const act = async (status) => {
    try { await api.patch(`/admin/bookings/${b.id}/status`, { status }); toast.success(`Marked ${statusLabel(status)}`); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const refund = async () => {
    try { await api.patch(`/admin/bookings/${b.id}/refund`); toast.success("Marked as refunded"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const canRefund = b.refund_status === "PENDING" || b.deposit_status === "PENDING_REFUND";
  const post = async (path, body, ok) => {
    try { await api.post(`/admin/bookings/${b.id}/${path}`, body); toast.success(ok); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const extend = async () => {
    const d = new Date(b.return_at);
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    const input = window.prompt("New return date & time (YYYY-MM-DDTHH:MM, your local time), paid in cash:", local);
    if (!input) return;
    const nd = new Date(input);
    if (isNaN(nd)) return toast.error("Enter a valid date & time");
    post("extend", { return_at: nd.toISOString() }, "Trip extended (cash)");
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="admin-booking-dialog">
        <DialogHeader><DialogTitle>Booking {b.id}</DialogTitle></DialogHeader>
        {b.status === "ACTIVE" && <LiveLocation bookingId={b.id} />}
        <div className="grid gap-4 text-sm sm:grid-cols-2">
          <div><p className="text-neutral-500">Customer</p><p className="font-medium">{b.customer_name}</p><p>{b.user_email} · {b.customer_phone}</p></div>
          <div><p className="text-neutral-500">Car</p><p className="font-medium">{b.car_name}</p><p>{b.pickup_location}</p></div>
          <div><p className="text-neutral-500">Pickup</p><p>{fmtDT(b.pickup_at)}</p></div>
          <div><p className="text-neutral-500">Return</p><p>{fmtDT(b.return_at)}</p></div>
          <div><p className="text-neutral-500">Status</p><StatusBadge status={b.status} testId="admin-booking-dialog-status" /></div>
          <div><p className="text-neutral-500">Payment</p><p>{b.payment_status}{b.cf_payment_id ? ` · ${b.cf_payment_id}` : ""}</p></div>
          <div><p className="text-neutral-500">Driver verification</p><div className="flex items-center gap-2"><KycBadge status={b.kyc_status} testId="admin-booking-kyc" /><Link to="/admin/verifications" className="text-xs text-[#FF5A00]">Review</Link></div></div>
          {b.refund_status && <div><p className="text-neutral-500">Refund</p><p>{inr(b.refund_amount)} · {b.refund_status}</p></div>}
          {b.deposit_status && <div><p className="text-neutral-500">Deposit</p><p>{inr(b.quote.deposit)} · {b.deposit_status}</p></div>}
          <div className="sm:col-span-2" data-testid="admin-booking-terms">
            <p className="text-neutral-500">Terms accepted</p>
            {b.terms_acceptance
              ? <p>Version {b.terms_acceptance.version} · {fmtDT(b.terms_acceptance.accepted_at)} · IP {b.terms_acceptance.ip || "—"}</p>
              : <p className="text-neutral-500">Not recorded (booked before acceptance logging)</p>}
          </div>
        </div>
        <div className="rounded-md bg-white/5 p-4 [&_*]:!text-neutral-200"><PriceRows q={b.quote} /></div>
        {b.checkin_selfie && <div className="sm:col-span-2" data-testid="admin-checkin-selfie"><p className="text-neutral-500">Check-in selfie</p><a href={`${api.defaults?.baseURL || ""}`} onClick={(e) => { e.preventDefault(); api.get(`/files/${b.checkin_selfie}`, { responseType: "blob" }).then((r) => window.open(URL.createObjectURL(r.data), "_blank")); }} className="text-xs text-[#0F5132] underline">View selfie taken at pickup</a></div>}
        {b.with_driver && !(["COMPLETED", "CANCELLED"].includes(b.status)) && <DriverAssign key={b.id} b={b} onChanged={onChanged} />}
        <InspectionEditor b={b} onSaved={onChanged} />
        <div>
          <p className="mb-2 text-sm font-semibold">History</p>
          <ul className="space-y-1 text-xs text-neutral-400">
            {(b.history || []).map((h) => <li key={`${h.at}-${h.status}-${h.note}`}>{fmtDT(h.at)} — {statusLabel(h.status)} · {h.note}</li>)}
          </ul>
        </div>
        <TripOtp b={b} />
        <div className="flex flex-wrap gap-2">
          {b.status === "PENDING_PAYMENT" && <button onClick={() => post("cash", {}, "Marked cash paid — booking confirmed")} data-testid="admin-booking-mark-cash" className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white">Mark cash paid</button>}
          {b.status === "CONFIRMED" && <button onClick={() => post("start", {}, "Trip started")} data-testid="admin-booking-start" className="rounded-md bg-[#0F5132] px-4 py-2 text-sm font-semibold text-white">Start trip (manual)</button>}
          {(b.status === "CONFIRMED" || b.status === "ACTIVE") && <button onClick={extend} data-testid="admin-booking-extend" className="rounded-md border border-[#D4AF67] px-4 py-2 text-sm font-semibold text-[#D4AF67]">Extend (cash)</button>}
          {b.status === "ACTIVE" && <button onClick={() => post("end", {}, "Trip ended")} data-testid="admin-booking-end" className="rounded-md bg-[#0F5132] px-4 py-2 text-sm font-semibold text-white">End trip (manual)</button>}
          {(NEXT[b.status] || []).map((s) => (
            <button key={s} onClick={() => act(s)} data-testid={`admin-booking-action-${s}`} className={`rounded-md px-4 py-2 text-sm font-semibold ${s === "CANCELLED" ? "border border-rose-500/50 text-rose-400" : "bg-[#FF5A00] text-white"}`}>{ACTION_LABEL[s]}</button>
          ))}
          {canRefund && <button onClick={refund} data-testid="admin-booking-mark-refunded" className="rounded-md border border-emerald-500/50 px-4 py-2 text-sm font-semibold text-emerald-400">Mark refund paid</button>}
          {b.payment_status === "PAID" && <Link to={`/invoice/${b.id}`} data-testid="admin-booking-invoice" className="rounded-md border border-white/20 px-4 py-2 text-sm font-semibold text-neutral-200">View invoice</Link>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewBookingDialog({ onClose, onCreated }) {
  const api = useApi();
  const [cars, setCars] = useState([]);
  const [f, setF] = useState({ user_email: "", customer_name: "", customer_phone: "", car_id: "", pickup_at: "", return_at: "", pickup_location: "", protection: true, with_driver: false, payment: "cash" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/cars").then((r) => setCars((r.data || []).filter((c) => c.active !== false))).catch(() => {}); }, []); // eslint-disable-line
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...f, pickup_at: new Date(f.pickup_at).toISOString(), return_at: new Date(f.return_at).toISOString() };
      await api.post("/admin/bookings/create", body);
      toast.success(f.payment === "cash" ? "Booking created & confirmed (cash)" : "Booking created — awaiting payment");
      onCreated(); onClose();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const inp = "w-full rounded-md border border-white/10 bg-[#141414] px-3 py-2 text-sm";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="admin-new-booking-dialog">
        <DialogHeader><DialogTitle>New booking (customer with approved KYC)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input className={inp} placeholder="Customer account email" value={f.user_email} onChange={set("user_email")} data-testid="nb-email" />
          <div className="grid grid-cols-2 gap-3">
            <input className={inp} placeholder="Customer name" value={f.customer_name} onChange={set("customer_name")} data-testid="nb-name" />
            <input className={inp} placeholder="Phone" value={f.customer_phone} onChange={set("customer_phone")} data-testid="nb-phone" />
          </div>
          <select className={inp} value={f.car_id} onChange={set("car_id")} data-testid="nb-car"><option value="">Select car</option>{cars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-neutral-400">Pickup<input type="datetime-local" className={inp} value={f.pickup_at} onChange={set("pickup_at")} data-testid="nb-pickup" /></label>
            <label className="text-xs text-neutral-400">Return<input type="datetime-local" className={inp} value={f.return_at} onChange={set("return_at")} data-testid="nb-return" /></label>
          </div>
          <input className={inp} placeholder="Pickup location (optional)" value={f.pickup_location} onChange={set("pickup_location")} />
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.protection} onChange={set("protection")} />Trip protection</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.with_driver} onChange={set("with_driver")} />With driver</label>
          </div>
          <select className={inp} value={f.payment} onChange={set("payment")} data-testid="nb-payment"><option value="cash">Cash collected — confirm now</option><option value="pending">Awaiting payment</option></select>
          <button onClick={submit} disabled={busy} data-testid="nb-submit" className="w-full rounded-md bg-[#0F5132] px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-60">{busy ? "Creating…" : "Create booking"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminBookings() {
  const api = useApi();
  const [list, setList] = useState([]);
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/bookings", { params: status ? { status } : {} }).then((r) => {
      setList(r.data);
      setSel((cur) => (cur ? r.data.find((x) => x.id === cur.id) || null : null));
    });
  }, [api, status]);
  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6" data-testid="admin-bookings">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-semibold tracking-tight">Bookings</h1>
        <div className="flex items-center gap-3">
          <button onClick={() => setCreating(true)} data-testid="admin-new-booking" className="rounded-md bg-[#0F5132] px-4 py-2 text-sm font-semibold text-white">+ New booking</button>
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="admin-bookings-filter" className="rounded-md border border-white/10 bg-[#141414] px-3 py-2 text-sm">
            {FILTERS.map((s) => <option key={s} value={s}>{s ? statusLabel(s) : "All statuses"}</option>)}
          </select>
        </div>
      </div>
      <div className="overflow-x-auto rounded-lg border border-white/10 bg-[#141414]">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>{["ID", "Customer", "Car", "Pickup → Return", "Total", "Verification", "Status"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {list.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-neutral-500" data-testid="admin-bookings-empty">No bookings found.</td></tr>}
            {list.map((b) => (
              <tr key={b.id} onClick={() => setSel(b)} className="cursor-pointer transition-colors duration-150 hover:bg-white/5" data-testid={`admin-booking-row-${b.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{b.id}</td>
                <td className="px-4 py-3"><p>{b.customer_name}</p><p className="text-xs text-neutral-500">{b.customer_phone}</p></td>
                <td className="px-4 py-3">{b.car_name}{b.with_driver && <span className="ml-2 rounded-full bg-[#FF5A00]/15 px-2 py-0.5 text-[10px] font-semibold text-[#FF8A3D]" data-testid={`admin-booking-driver-badge-${b.id}`}>{b.driver ? "Driver assigned" : "Needs driver"}</span>}</td>
                <td className="px-4 py-3 text-xs text-neutral-400">{fmtDT(b.pickup_at)}<br />{fmtDT(b.return_at)}</td>
                <td className="px-4 py-3">{inr(b.quote.total)}</td>
                <td className="px-4 py-3"><KycBadge status={b.kyc_status} /></td>
                <td className="px-4 py-3"><StatusBadge status={b.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <BookingDialog b={sel} onClose={() => setSel(null)} onChanged={load} />
      {creating && <NewBookingDialog onClose={() => setCreating(false)} onCreated={load} />}
    </div>
  );
}
